/**
 * Meta Pixel utility functions
 */

import { META_PIXEL_CONFIG, isPixelEnabled } from '../config/metaPixel';

// Check if fbq is available and tracking is enabled
const isFbqAvailable = () => {
  const pixelEnabled = isPixelEnabled();
  const windowAvailable = typeof window !== 'undefined';
  const fbqAvailable = windowAvailable && window.fbq;
  
  if (!pixelEnabled) {
    console.warn('Meta Pixel: Tracking disabled - pixel not enabled');
  }
  if (!windowAvailable) {
    console.warn('Meta Pixel: Tracking disabled - window not available');
  }
  if (windowAvailable && !fbqAvailable) {
    console.warn('Meta Pixel: Tracking disabled - fbq function not available');
  }
  
  return pixelEnabled && windowAvailable && fbqAvailable;
};

// Standard Events
export const trackPageView = () => {
  if (isFbqAvailable()) {
    window.fbq('track', 'PageView');
  }
};

export const trackViewContent = (contentData = {}) => {
  if (isFbqAvailable()) {
    const data = {
      content_type: contentData.content_type || 'product',
      content_ids: contentData.content_ids || [],
      content_name: contentData.content_name || '',
      content_category: contentData.content_category || '',
      value: contentData.value || 0,
      currency: contentData.currency || META_PIXEL_CONFIG.DEFAULT_CURRENCY,
      ...contentData
    };
    
    // Debug logging
    console.log('Meta Pixel: trackViewContent called with data:', data);
    
    window.fbq('track', 'ViewContent', data);
  } else {
    console.warn('Meta Pixel: trackViewContent called but fbq not available');
  }
};

export const trackAddToCart = (cartData = {}) => {
  if (isFbqAvailable()) {
    const data = {
      content_type: cartData.content_type || 'product',
      content_ids: cartData.content_ids || [],
      content_name: cartData.content_name || '',
      content_category: cartData.content_category || '',
      value: cartData.value || 0,
      currency: cartData.currency || META_PIXEL_CONFIG.DEFAULT_CURRENCY,
      ...cartData
    };
    window.fbq('track', 'AddToCart', data);
  }
};

export const trackInitiateCheckout = (checkoutData = {}) => {
  if (isFbqAvailable()) {
    const data = {
      content_type: checkoutData.content_type || 'product',
      content_ids: checkoutData.content_ids || [],
      value: checkoutData.value || 0,
      currency: checkoutData.currency || META_PIXEL_CONFIG.DEFAULT_CURRENCY,
      num_items: checkoutData.num_items || 1,
      ...checkoutData
    };
    window.fbq('track', 'InitiateCheckout', data);
  }
};

export const trackPurchase = (purchaseData = {}) => {
  if (isFbqAvailable()) {
    const data = {
      content_type: purchaseData.content_type || 'product',
      content_ids: purchaseData.content_ids || [],
      value: purchaseData.value || 0,
      currency: purchaseData.currency || META_PIXEL_CONFIG.DEFAULT_CURRENCY,
      num_items: purchaseData.num_items || 1,
      ...purchaseData
    };
    window.fbq('track', 'Purchase', data);
  }
};

export const trackAddToWishlist = (wishlistData = {}) => {
  if (isFbqAvailable()) {
    const data = {
      content_type: wishlistData.content_type || 'product',
      content_ids: wishlistData.content_ids || [],
      content_name: wishlistData.content_name || '',
      content_category: wishlistData.content_category || '',
      value: wishlistData.value || 0,
      currency: wishlistData.currency || META_PIXEL_CONFIG.DEFAULT_CURRENCY,
      ...wishlistData
    };
    window.fbq('track', 'AddToWishlist', data);
  }
};

export const trackSearch = (searchData = {}) => {
  if (isFbqAvailable()) {
    const data = {
      search_string: searchData.search_string || '',
      content_type: searchData.content_type || 'product',
      content_ids: searchData.content_ids || [],
      ...searchData
    };
    window.fbq('track', 'Search', data);
  }
};

export const trackLead = (leadData = {}) => {
  if (isFbqAvailable()) {
    const data = {
      content_name: leadData.content_name || '',
      content_category: leadData.content_category || '',
      value: leadData.value || 0,
      currency: leadData.currency || META_PIXEL_CONFIG.DEFAULT_CURRENCY,
      ...leadData
    };
    window.fbq('track', 'Lead', data);
  }
};

export const trackCompleteRegistration = (registrationData = {}) => {
  if (isFbqAvailable()) {
    const data = {
      content_name: registrationData.content_name || '',
      status: registrationData.status || true,
      ...registrationData
    };
    window.fbq('track', 'CompleteRegistration', data);
  }
};

// Custom Events
export const trackCustomEvent = (eventName, eventData = {}) => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', eventName, eventData);
  }
};

// Helper function to determine product availability status based on backend logic
const getProductAvailability = (product) => {
  // First check approval status - rejected products are not available
  if (product.approvalStatus === 'rejected') {
    return 'discontinued';
  }

  // Check if product is pending approval
  if (product.approvalStatus === 'pending') {
    return 'pending';
  }

  // Check if product is a draft (not yet published)
  if (product.isDraft === true || product.status === 'draft') {
    return 'pending';
  }

  // Check if product is inactive (admin disabled)
  if (product.status === 'inactive') {
    return 'discontinued';
  }

  // Check if product is explicitly marked as out-of-stock
  if (product.status === 'out-of-stock') {
    return 'out of stock';
  }

  // For active products, check actual stock levels
  if (product.status === 'active') {
    // Check main product stock
    if (product.stock !== undefined) {
      if (product.stock <= 0) {
        return 'out of stock';
      } else {
        return 'in stock';
      }
    }

    // Check variants stock if product has variants
    if (product.variants && product.variants.length > 0) {
      const totalVariantStock = product.variants.reduce((total, variant) => total + (variant.stock || 0), 0);
      if (totalVariantStock <= 0) {
        return 'out of stock';
      } else {
        return 'in stock';
      }
    }

    // If active but no stock info, assume available for order
    return 'available for order';
  }

  // Default fallback for any unknown status
  return 'out of stock';
};

// Helper function to get the most accurate price from product model
const getProductPrice = (product) => {
  // Priority order based on your backend model:
  // 1. effectivePrice (virtual field that considers all discounts)
  // 2. specialOfferPrice (if available and lower than regular)
  // 3. regularPrice (base price)
  // 4. Fallback to 0

  if (product.effectivePrice !== undefined && product.effectivePrice > 0) {
    return product.effectivePrice;
  }

  if (product.specialOfferPrice !== undefined && product.specialOfferPrice > 0 &&
      product.regularPrice !== undefined && product.specialOfferPrice < product.regularPrice) {
    return product.specialOfferPrice;
  }

  if (product.regularPrice !== undefined && product.regularPrice > 0) {
    return product.regularPrice;
  }

  // Fallback for legacy data or edge cases
  if (product.price !== undefined && product.price > 0) {
    return product.price;
  }

  return 0;
};

// Helper function to extract category name from product
const getProductCategory = (product) => {
  // Handle populated category objects
  if (product.categories && product.categories.length > 0) {
    const firstCategory = product.categories[0];

    // If category is populated (object with name)
    if (typeof firstCategory === 'object' && firstCategory.name) {
      return firstCategory.name;
    }

    // If category is just an ID string
    if (typeof firstCategory === 'string') {
      return firstCategory;
    }
  }

  // Legacy field name
  if (product.category) {
    return typeof product.category === 'object' ? product.category.name || '' : product.category;
  }

  if (product.categoryName) {
    return product.categoryName;
  }

  return '';
};

// Helper function to extract brand name from product
const getProductBrand = (product) => {
  // Handle populated brand object
  if (product.brand) {
    if (typeof product.brand === 'object' && product.brand.name) {
      return product.brand.name;
    }

    if (typeof product.brand === 'string') {
      return product.brand;
    }
  }

  if (product.brandName) {
    return product.brandName;
  }

  return '';
};

// Helper function to format product data for tracking
export const formatProductData = (product) => {
  // Debug logging to help identify issues
  if (!product) {
    console.warn('Meta Pixel: formatProductData called with null/undefined product');
    return null;
  }

  // Use comprehensive price logic that respects your backend model
  const price = getProductPrice(product);
  const formattedPrice = `${price.toFixed(2)} ${META_PIXEL_CONFIG.DEFAULT_CURRENCY}`;

  // Get product ID (MongoDB _id is the primary identifier)
  const productId = product._id || product.id || '';
  
  // Debug logging for missing ID
  if (!productId) {
    console.warn('Meta Pixel: Product missing ID field', {
      product: product,
      hasId: !!product.id,
      hasUnderscoreId: !!product._id,
      keys: Object.keys(product)
    });
  }

  // Get category and brand names properly
  const categoryName = getProductCategory(product);
  const brandName = getProductBrand(product);

  return {
    // Required Meta Pixel fields
    id: productId,
    content_ids: [productId],
    content_name: product.name || product.title || '',
    content_category: categoryName,
    value: price,
    currency: META_PIXEL_CONFIG.DEFAULT_CURRENCY,
    price: formattedPrice,
    availability: getProductAvailability(product),

    // Enhanced fields for better tracking
    content_type: 'product',
    brand: brandName,

    // Additional product information for Meta Pixel
    ...(product.sku && { sku: product.sku }),
    ...(product.rating && { rating: product.rating }),
    ...(product.reviewCount && { num_ratings: product.reviewCount }),
    ...(product.discountPercentage && product.discountPercentage > 0 && {
      discount_percentage: product.discountPercentage
    })
  };
};

// Helper function to format cart data for tracking
export const formatCartData = (cartItems) => {
  if (!cartItems || cartItems.length === 0) {
    return {
      id: '',
      content_ids: [],
      value: 0,
      currency: META_PIXEL_CONFIG.DEFAULT_CURRENCY,
      num_items: 0,
      content_type: 'product',
      price: `0.00 ${META_PIXEL_CONFIG.DEFAULT_CURRENCY}`,
      availability: 'out of stock'
    };
  }

  // Map content IDs properly
  const content_ids = cartItems.map(item => {
    // Try to get product ID from various possible sources
    return item.product?._id || item.product?.id || item.productId || item._id || item.id || '';
  });

  // Calculate total value using proper price logic
  const value = cartItems.reduce((total, item) => {
    let itemPrice = 0;

    // If we have full product data, use comprehensive price logic
    if (item.product) {
      itemPrice = getProductPrice(item.product);
    } else {
      // Fallback to item price
      itemPrice = item.price || 0;
    }

    return total + (itemPrice * (item.quantity || 1));
  }, 0);

  const num_items = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

  // Get the first item for availability and price formatting (Meta Pixel uses single values for these)
  const firstItem = cartItems[0];
  let availability = 'in stock';
  let formattedPrice = `${value.toFixed(2)} ${META_PIXEL_CONFIG.DEFAULT_CURRENCY}`;
  let primaryId = '';
  let categoryName = '';
  let brandName = '';

  if (firstItem) {
    primaryId = firstItem.product?._id || firstItem.product?.id || firstItem.productId || firstItem._id || firstItem.id || '';

    // If the item has full product data, use comprehensive logic
    if (firstItem.product) {
      availability = getProductAvailability(firstItem.product);
      const itemPrice = getProductPrice(firstItem.product);
      formattedPrice = `${itemPrice.toFixed(2)} ${META_PIXEL_CONFIG.DEFAULT_CURRENCY}`;
      categoryName = getProductCategory(firstItem.product);
      brandName = getProductBrand(firstItem.product);
    } else {
      // For simple cart items, assume in stock if price exists
      availability = (firstItem.price && firstItem.price > 0) ? 'in stock' : 'out of stock';
      formattedPrice = `${(firstItem.price || 0).toFixed(2)} ${META_PIXEL_CONFIG.DEFAULT_CURRENCY}`;
    }
  }

  return {
    // Required Meta Pixel fields
    id: primaryId,
    content_ids,
    value,
    currency: META_PIXEL_CONFIG.DEFAULT_CURRENCY,
    num_items,
    content_type: 'product',
    price: formattedPrice,
    availability: availability,

    // Additional fields for better tracking
    ...(categoryName && { content_category: categoryName }),
    ...(brandName && { brand: brandName }),
    ...(firstItem?.product?.name && { content_name: firstItem.product.name })
  };
};
